import { Box } from '@mui/material';
import { getCategory, isCategoryAvailable } from '@timelog/pm-shared';

import { AddCategoryTile } from './AddCategoryTile';
import { CategoryCard } from './CategoryCard';
import { useFinancials } from './editor';
import { useGuidedEntry } from './flow';
import { OwnWorkCard } from './OwnWorkCard';
import { SplitRowsSection } from './SplitRowsSection';
import { SupplierSection } from './SupplierSection';

/**
 * The bento: Own Work spans two columns, Contingency beside it, Expenses,
 * Sub-contractors (span 2) and Materials below, and the Add-category tile for
 * anything hidden (BR-9).
 *
 * The grid reflows on its own width, not the viewport's, because it lives inside a
 * resizable panel — a media query would be measuring the wrong box. Web solves
 * the same problem the same way in `components/layouts/CardGrid`
 * (`container-type: inline-size` plus `@container` queries).
 *
 * It was previously a fixed `1fr 1fr 1fr`. `1fr` means `minmax(auto, 1fr)`, so
 * each track's floor is its content's min-content width: the tracks refused to
 * shrink, the grid grew past the panel, and the cards clipped on the right.
 *
 * `auto-fit`/`minmax` alone would not have worked here — a `span 2` card in a
 * one-column grid creates an implicit second column and overflows again — hence
 * the explicit steps below, which drop the spans at the narrowest size.
 */
/**
 * The container being measured. It has to be a *separate element* from the grid:
 * an element is never its own container-query container, so putting
 * `containerType` and the `@container` rules on one Box silently does nothing.
 * Web splits `.GridContainer` from `.Grid` for the same reason.
 */
const GRID_CONTAINER_SX = { containerType: 'inline-size', mt: 1.5 } as const;

const CATEGORY_GRID_SX = {
  display: 'grid',
  gap: 1,
  // Narrowest: one column, and nothing spans.
  gridTemplateColumns: '1fr',
  '& > *': { gridColumn: 'span 1' },
  /*
   * The steps are set by the widest card header, measured rather than guessed:
   * 8px dot + 8 gap + "Sub-contractors" (~105px at 14px) + 4 gap + factor chip
   * (~40) + 4 + kebab (20) = ~189px of content, so a card needs ~221px.
   *
   *   two columns  → 2×221 + 8  = 450
   *   three columns → 3×221 + 16 = 680
   *
   * Three columns below 680 forced the label to ellipsis ("Continge…"), which
   * looks as broken as the overflow it replaced. Collapsing the contracts rail
   * frees 152px, which is the discoverable way to get the three-column bento back
   * on a narrower window.
   */
  '@container (min-width: 450px)': {
    gridTemplateColumns: '1fr 1fr',
    // Two columns is wide enough for the span-2 cards to keep their emphasis.
    '& > [data-category-span="2"]': { gridColumn: 'span 2' },
  },
  '@container (min-width: 680px)': {
    gridTemplateColumns: '1fr 1fr 1fr',
  },
} as const;

export const CategoryGrid = () => {
  const { state, budgetModel } = useFinancials();
  const guided = useGuidedEntry();
  // Availability (BR-27) always wins; below that, the card's own `hidden`. The
  // walk never steps INTO a hidden card (it skips them), but one hidden from its
  // kebab while it is the active step stays up until the walk moves on —
  // otherwise the walk would sit on a step with nothing on screen.
  const visible = (id: 'own' | 'cont' | 'exp' | 'sub' | 'mat') =>
    isCategoryAvailable(budgetModel.config, id) &&
    (!getCategory(state.document, id).hidden || guided.activeStepFor(id) !== null);

  return (
    <Box sx={GRID_CONTAINER_SX}>
      <Box data-automation-id="category-grid" sx={CATEGORY_GRID_SX}>
        {visible('own') ? <OwnWorkCard /> : null}
        {visible('cont') ? <CategoryCard categoryId="cont" /> : null}
        {visible('exp') ? (
          <CategoryCard categoryId="exp" footer={<SplitRowsSection categoryId="exp" />} />
        ) : null}
        {visible('sub') ? (
          <CategoryCard categoryId="sub" span={2} footer={<SupplierSection />} />
        ) : null}
        {visible('mat') ? (
          <CategoryCard categoryId="mat" footer={<SplitRowsSection categoryId="mat" />} />
        ) : null}
        <AddCategoryTile />
      </Box>
    </Box>
  );
};
